/**
 * @file ProposeAmendmentDialog.jsx
 * @module ui/src/components/tenant-admin/ProposeAmendmentDialog
 * @description
 *   Collects the economic effective date and reason required to begin a plan amendment.
 *   It previews the resulting temporal boundary between the current approved version
 *   and the proposed successor before any amendment record is created.
 *
 * @version 1.0.0
 * @since S755
 * @author data-fit SME Build · Reviewed: Claude S755
 *
 * @architecture
 *   Locked decisions : #1, #25
 *   Temporal invariants : INV-1, INV-3
 *   Design principles : DP-1, DP-9, DP-10
 *   Cross-cutting contracts : none
 *
 * @domain-rules
 *   - Amendment economic effective date determines when the successor version governs events.
 *   - The amendment effective date is not an approval date or submission date.
 *   - Current and proposed versions are resolved by economic event date.
 *   - A plan effective_from value, when present, establishes the lower bound for amendment effective date.
 *   - Beginning an amendment is a server-authoritative governance mutation with no optimistic state.
 *
 * @session-6-migration-flags
 *   - effective_from on plan — pending m_tc_01
 *   - effective_to on plan — pending m_tc_01
 *
 * @changelog
 *   1.0.0  S755  Initial build — Group D batch delivery
 */

import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

/**
 * @typedef {Object} AmendmentPlanContext
 * @property {number} planId
 * @property {string} planCode
 * @property {string} planName
 * @property {number} version
 * @property {string} effectiveFrom
 * @property {string|null} effectiveTo
 */

/**
 * @typedef {Object} ProposeAmendmentDialogProps
 * @property {AmendmentPlanContext} plan
 * @property {boolean} open
 * @property {(amendmentId: string) => void} onBegun
 * @property {() => void} onCancel
 */

const styles = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    display: 'grid',
    placeItems: 'center',
    background: 'rgb(var(--color-bg))',
    zIndex: 1000,
    padding: '20px',
  },
  dialog: {
    width: 'min(660px, 100%)',
    maxHeight: '90vh',
    overflowY: 'auto',
    padding: '22px',
    border: '1px solid rgb(var(--color-border))',
    borderRadius: '8px',
    background: 'rgb(var(--color-surface))',
    color: 'rgb(var(--color-text))',
    fontFamily: '"IBM Plex Sans", "Segoe UI", Arial, sans-serif',
    fontSize: 'var(--font-sz-base)',
  },
  heading: {
    margin: 0,
    fontSize: '21px',
  },
  muted: {
    color: 'rgb(var(--color-text-muted))',
  },
  field: {
    display: 'grid',
    gap: '6px',
    marginTop: '16px',
  },
  input: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '9px 10px',
    border: '1px solid rgb(var(--color-border))',
    borderRadius: '5px',
    background: 'rgb(var(--color-surface))',
    color: 'rgb(var(--color-text))',
    font: 'inherit',
  },
  textarea: {
    width: '100%',
    minHeight: '110px',
    boxSizing: 'border-box',
    padding: '9px 10px',
    border: '1px solid rgb(var(--color-border))',
    borderRadius: '5px',
    background: 'rgb(var(--color-surface))',
    color: 'rgb(var(--color-text))',
    font: 'inherit',
    resize: 'vertical',
  },
  preview: {
    marginTop: '18px',
    padding: '14px',
    borderLeft: '4px solid rgb(var(--color-info))',
    background: 'rgb(var(--color-surface-muted))',
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    marginTop: '22px',
  },
};

function getFocusableElements(container) {
  // WHY: Standard focusable elements define the modal keyboard loop.
  if (!container) {
    return [];
  }

  return Array.from(
    container.querySelectorAll(
      'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
    ),
  );
}

function subtractOneDay(isoDate) {
  // WHY: The preview needs the calendar day immediately before the proposed economic
  // effective date; UTC arithmetic avoids local timezone rollover changing the ISO date.
  const date = new Date(`${isoDate}T00:00:00Z`);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  date.setUTCDate(date.getUTCDate() - 1);
  return date.toISOString().slice(0, 10);
}

/**
 * @param {ProposeAmendmentDialogProps} props
 * @returns {React.ReactElement|null}
 */
export default function ProposeAmendmentDialog({
  plan,
  open,
  onBegun,
  onCancel,
}) {
  // WHY: Economic effective date is the core temporal input that determines event governance.
  const [effectiveDate, setEffectiveDate] = useState('');

  // WHY: Amendment reason is required governance context persisted with the amendment.
  const [reason, setReason] = useState('');

  // WHY: Pending state prevents duplicate amendment creation while awaiting server authority.
  const [submitting, setSubmitting] = useState(false);

  // WHY: Server and temporal validation errors must remain visible without optimistic navigation.
  const [error, setError] = useState(null);

  const dialogRef = useRef(null);
  const effectiveDateRef = useRef(null);

  // WHY: Reopening the dialog represents a new amendment proposal and therefore resets draft state.
  useEffect(() => {
    if (!open) {
      return undefined;
    }

    setEffectiveDate('');
    setReason('');
    setSubmitting(false);
    setError(null);

    const focusTimer = window.setTimeout(() => {
      effectiveDateRef.current?.focus();
    }, 0);

    return () => {
      window.clearTimeout(focusTimer);
    };
  }, [open]);

  // WHY: Modal keyboard behavior must prevent focus escape and map Escape to explicit cancellation.
  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      // WHY: Escape abandons the unsubmitted proposal through the parent callback.
      if (event.key === 'Escape') {
        event.preventDefault();
        onCancel();
        return;
      }

      // WHY: Non-Tab keyboard input does not participate in focus trapping.
      if (event.key !== 'Tab') {
        return;
      }

      const focusable = getFocusableElements(dialogRef.current);

      if (focusable.length === 0) {
        event.preventDefault();
        dialogRef.current?.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      // WHY: Reverse navigation from the first control wraps to the last modal control.
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      }

      // WHY: Forward navigation from the final control wraps to the first modal control.
      if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onCancel]);

  // WHY: Both governance inputs are mandatory, and when the plan temporal lower bound exists
  // the amendment must begin strictly after that date.
  const effectiveDateValid =
    effectiveDate.length > 0 &&
    (!plan.effectiveFrom || effectiveDate > plan.effectiveFrom);

  const formValid =
    effectiveDateValid &&
    reason.trim().length > 0;

  const priorDay = effectiveDate ? subtractOneDay(effectiveDate) : '';

  // WHY: Date editing updates local proposal state only; creation remains deferred to the API mutation.
  const handleEffectiveDateChange = (event) => {
    setEffectiveDate(event.target.value);
    setError(null);
  };

  // WHY: Reason editing is local draft state and should clear stale request errors.
  const handleReasonChange = (event) => {
    setReason(event.target.value);
    setError(null);
  };

  // WHY: Cancel delegates closure and trigger-focus restoration to the parent.
  const handleCancel = () => {
    if (!submitting) {
      onCancel();
    }
  };

  // WHY: Beginning an amendment creates a governed record. onBegun is therefore invoked
  // only after a successful server response containing an amendment identifier.
  const handleBeginAmendment = async () => {
    if (!formValid || submitting) {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // WHY: This endpoint creates the draft amendment using the economic effective date
      // and reason collected here; local preview state is not authoritative.
      const response = await fetch(
        `/api/v1/plans/${plan.planId}/amendments/begin`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            economicEffectiveDate: effectiveDate,
            amendmentReason:       reason.trim(),
          }),
        },
      );

      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        throw new Error(
          payload?.detail ||
            payload?.message ||
            'The amendment could not be created.',
        );
      }

      const payload = await response.json();

      if (
        typeof payload.amendmentId !== 'string' ||
        payload.amendmentId.length === 0
      ) {
        throw new Error(
          'The server did not return the amendment identifier.',
        );
      }

      onBegun(payload.amendmentId);
    } catch (requestError) {
      setError(
        requestError instanceof Error
          ? requestError.message
          : 'The amendment could not be created.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  // WHY: Closed controlled dialogs are removed so they cannot capture focus or expose stale proposal state.
  if (!open) {
    return null;
  }

  return (
    <div style={styles.backdrop}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="propose-amendment-title"
        tabIndex={-1}
        style={styles.dialog}
      >
        <h2 id="propose-amendment-title" style={styles.heading}>
          Propose amendment
        </h2>

        <p style={styles.muted}>
          Current approved plan: {plan.planName} (V{plan.version})
        </p>

        <label style={styles.field}>
          <span>Amendment economic effective date</span>
          <input
            ref={effectiveDateRef}
            style={styles.input}
            type="date"
            value={effectiveDate}
            onChange={handleEffectiveDateChange}
            required
          />
        </label>

        {/* WHY: A known plan effective_from creates a real temporal validation boundary;
            this message is not shown for pre-migration records where the value is absent. */}
        {effectiveDate && plan.effectiveFrom && !effectiveDateValid ? (
          <p
            className="modifier-manager-inline-error"
            role="alert"
          >
            The amendment economic effective date must be after{' '}
            {plan.effectiveFrom}.
          </p>
        ) : null}

        <label style={styles.field}>
          <span>Amendment Reason</span>
          <textarea
            style={styles.textarea}
            value={reason}
            onChange={handleReasonChange}
            required
          />
        </label>

        {/* WHY: Temporal preview appears only after a usable economic effective date exists;
            it explains event governance before the amendment record is created. */}
        {effectiveDate && priorDay ? (
          <div style={styles.preview}>
            <strong>Temporal preview</strong>

            {/* WHY: Pre-migration plans may not yet carry effective_from, so the fallback
                copy preserves the governance concept without fabricating a start date. */}
            {plan.effectiveFrom ? (
              <p>
                Current V{plan.version} governs economic events from{' '}
                {plan.effectiveFrom} through {priorDay}. The proposed amendment
                will govern events from {effectiveDate} onward.
              </p>
            ) : (
              <p>
                Current V{plan.version} will govern events up to {priorDay}. The
                proposed amendment will govern events from {effectiveDate} onward.
              </p>
            )}
          </div>
        ) : null}

        {/* WHY: Request failure belongs to amendment creation, not the temporal preview itself. */}
        {error ? (
          <p
            className="modifier-manager-inline-error"
            role="alert"
          >
            {error}
          </p>
        ) : null}

        <div style={styles.actions}>
          <button
            type="button"
            className="secondary-button"
            disabled={submitting}
            onClick={handleCancel}
          >
            Cancel
          </button>

          <button
            type="button"
            className="primary-button"
            disabled={!formValid || submitting}
            onClick={handleBeginAmendment}
          >
            {submitting ? 'Creating amendment…' : 'Begin amendment'}
          </button>
        </div>
      </div>
    </div>
  );
}

ProposeAmendmentDialog.propTypes = {
  plan: PropTypes.shape({
    planId: PropTypes.number.isRequired,
    planCode: PropTypes.string.isRequired,
    planName: PropTypes.string.isRequired,
    version: PropTypes.number.isRequired,
    effectiveFrom: PropTypes.string,
    effectiveTo: PropTypes.string,
  }).isRequired,
  open: PropTypes.bool.isRequired,
  onBegun: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

/**
 * @example
 * <ProposeAmendmentDialog
 *   plan={{
 *     planId: 418,
 *     planCode: 'AE-ENT-2026',
 *     planName: 'Enterprise AE FY26',
 *     version: 3,
 *     effectiveFrom: '2026-01-01',
 *     effectiveTo: null,
 *   }}
 *   open={amendmentDialogOpen}
 *   onBegun={(amendmentId) => navigate(`/amendments/${amendmentId}`)}
 *   onCancel={() => setAmendmentDialogOpen(false)}
 * />
 */

/**
 * DOMAIN COMPLIANCE CHECK
 * - Economic effective date is the temporal governing input.
 * - Effective date is never labeled as an approval or submission date.
 * - Live preview uses the locked current-version/successor-version event-attribution language.
 * - Pre-migration absence of plan.effectiveFrom has an explicit fallback.
 * - Both effective date and amendment reason are required before creation.
 * - onBegun executes only after authoritative server success.
 */

/**
 * SESSION 6 MIGRATION FLAGS
 * - effective_from on plan — pending m_tc_01
 * - effective_to on plan — pending m_tc_01
 */

/**
 * ACCESSIBILITY NOTES
 * - Dialog exposes role="dialog", aria-modal, and an accessible heading.
 * - Focus enters the economic effective date field on open.
 * - Tab and Shift+Tab are trapped inside the modal.
 * - Escape delegates to onCancel.
 * - Validation and server errors use role="alert".
 * - Parent is responsible for returning focus to the opening trigger after cancellation or success.
 */
